import { ColumnData } from "@/entities/dataTables/types";
import { ViewDataConfiguration, ViewType } from "@/entities/views/types";
import { normalizeFiltersAfterDeserialization } from "@/entities/views/helpers";
import { Filters, SortQuery } from "@/services/api/types";
import { VisibilityState } from "@tanstack/react-table";
import i18n, { TFunction } from "i18next";
import { APIFieldItem, SettingsFieldType } from "@/pages/settings/types";
import { FieldDisplayType } from "@/pages/settings/components/dealsSettings/FieldManagement/types";
import { isEqual, keyBy } from "lodash";
import {
  DataTableFilter,
  FilterValue
} from "@/components/controls/DataTableFilters/DataTableFilters";
import MultiSelectBadgesCell from "@/components/controls/DataTable/MultiSelectBadgesCell";
import UrlCell from "@/components/controls/DataTable/UrlCell";
import Text from "@/components/ui/text";
import { formatNumber } from "@/utils/numbers";
import { monetarySortingFn, stripHtml } from "@/utils/strings";
import { simpleDate, toLocalDate } from "@/utils/datetime";
import {
  getConfiguredCurrencySymbol,
  parseOptions
} from "@/utils/entityFieldsUtils/shared";
import { tWithFallback } from "@/utils/i18n";
import {
  DATE_BUCKETS,
  DATE_BUCKET_SEPARATOR,
  DateBucket
} from "@/entities/dataTables/monthGrouping";
import { CurrencyGroupCode } from "@/entities/dataTables/currencyGrouping";
import { parseFileFieldValue } from "@/utils/entityFieldsUtils/fileFieldUtils";
import { createSwitchOptions } from "@/utils/fieldRendering/shared";
import {
  getEntityTypeFromDisplayType,
  isEntityDisplayType,
  isLicensesDisplayType,
  isProductsDisplayType
} from "@/utils/entityFieldHelpers";
import { camelizeFieldKey } from "@/utils/fieldKeys";
import IcpFitValue from "@/components/controls/DetailedView/ICPFit/IcpFitValue";
import { termsFieldName } from "@/entities/dataTables/termsCurrency";
import { NON_FILTERABLE_CONTRACT_ROLLUP_FIELD_KEYS } from "@/entities/dataTables/contractRollupFields";
import {
  isIcpFitFieldKey,
  ICP_FIT_PEOPLE_COMPANY_KEYS,
  ICP_FIT_LEAD_KEYS
} from "@/entities/icpFit/helpers";
import { ReactNode } from "react";

export const FLEX_SNAPSHOT_PREFIX = "flex_snapshot_";

/**
 * Return columns saved by a previous resize for this view type, usable before
 * selectedView.id is known. Enables synchronous columnsState init so the flex
 * seed fires before first paint instead of after.
 */
export const getFlexSnapshot = (viewType: ViewType): ColumnData[] => {
  try {
    const str = sessionStorage.getItem(`${FLEX_SNAPSHOT_PREFIX}${viewType}`);
    if (str) {
      const parsed = JSON.parse(str);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch {
    // Private mode or parse error — ignore.
  }
  return [];
};

/**
 * Resolve a linked-field cell value. Linked fields are surfaced by the API as a
 * separate `<linkedKey>_resolved` field next to the raw `<linkedKey>` id, with
 * the display name as a sibling. Prefer the resolved value, fall back to the
 * display field. Mirrors the resolution rule used by the generic field-driven
 * column pipeline below so hardcoded column cells stay consistent.
 */
export const resolveLinkedCellValue = (
  row: Record<string, unknown>,
  linkedKey: string,
  displayKey: string
): string => {
  const resolved = row[`${linkedKey}_resolved`];
  if (resolved != null && resolved !== "") return String(resolved);
  const display = row[displayKey];
  return display != null ? String(display) : "";
};

export const VIEW_PREFIX = "view-";
export const SELECTED_VIEW_PREFIX = "selected-view-";

export const saveSelectedViewId = (viewType: number, viewId: string): void => {
  if (!viewId) return;
  try {
    sessionStorage.setItem(`${SELECTED_VIEW_PREFIX}${viewType}`, viewId);
  } catch (e) {
    console.error("Error saving selected view ID:", e);
  }
};

export const getSelectedViewId = (viewType: number): string | undefined => {
  try {
    const viewId = sessionStorage.getItem(`${SELECTED_VIEW_PREFIX}${viewType}`);
    return viewId || undefined;
  } catch (e) {
    console.error("Error getting selected view ID:", e);
    return undefined;
  }
};

export const clearSelectedViewId = (viewType: number): void => {
  try {
    sessionStorage.removeItem(`${SELECTED_VIEW_PREFIX}${viewType}`);
  } catch (e) {
    console.error("Error clearing selected view ID:", e);
  }
};

// Serialize the full column set for persistence — including hidden columns with
// isVisible:false. Persisting hidden columns (rather than stripping them) is what
// keeps a user-removed column removed after save/reload: mergeColumnsWithDefaults
// then only re-adds columns genuinely absent from the saved set (new default
// fields), never ones the user explicitly hid. order is re-based on current
// position so drag-reordering also persists.
export const serializeColumnsForSave = (
  columns: ColumnData[]
): ColumnData[] => {
  return columns.map((column, index) => ({
    ...column,
    order: index
  }));
};

export const mergeColumnsWithDefaults = (
  savedColumns: ColumnData[] | undefined,
  defaultColumns: ColumnData[]
): ColumnData[] => {
  if (!savedColumns || savedColumns.length === 0) {
    return defaultColumns;
  }

  const defaultColumnsMap = new Map(
    defaultColumns.map((column) => [column.id, column])
  );
  const savedColumnIds = new Set(savedColumns.map((column) => column.id));

  // Saved columns first, in their persisted order, so a drag-reorder survives
  // reload (getVisibleExportColumns sorts by the order re-based below). Only
  // the user's own state (visibility, order, width) carries over from the
  // saved copy — everything else comes from the current default so refreshed
  // metadata (e.g. a renamed label) wins instead of the stale saved value. A
  // saved column no longer present in defaults (a removed field) is dropped.
  const orderedSaved = savedColumns
    .map((savedColumn) => {
      const defaultColumn = defaultColumnsMap.get(savedColumn.id);
      if (!defaultColumn) {
        return null;
      }
      return {
        ...defaultColumn,
        isVisible: savedColumn.isVisible,
        order: savedColumn.order,
        ...(savedColumn.flex !== undefined && { flex: savedColumn.flex })
      };
    })
    .filter((column): column is ColumnData => column !== null);

  // Genuinely new default fields (e.g. a SYSTEM field added after this view was
  // saved) are appended in default order, inheriting defaultCol.isVisible so they
  // appear checked. Columns the user explicitly hid are persisted (isVisible:false)
  // by serializeColumnsForSave, so they are matched above and stay hidden instead
  // of reappearing here.
  const newDefaults = defaultColumns.filter(
    (column) => !savedColumnIds.has(column.id)
  );

  return [...orderedSaved, ...newDefaults].map((column, index) => ({
    ...column,
    order: index
  }));
};

// Visible, non-excluded columns in display order — the exact set the CSV export ships.
export const getVisibleExportColumns = (
  columnsState: ColumnData[],
  excludeFromExport?: readonly string[]
): ColumnData[] => {
  const excluded = new Set(excludeFromExport ?? []);
  return columnsState
    .filter((column) => column.isVisible && !excluded.has(column.id))
    .sort(
      (firstColumn, secondColumn) => firstColumn.order - secondColumn.order
    );
};

// A temp view records the server config the tab was showing when it drafted
// it, so a draft left over from before the view was saved elsewhere (another
// tab, an earlier session) is discarded instead of replacing the newer saved
// config. It is judged only the first time this tab loads the view (a new page,
// a duplicated or restored tab). Inside a tab the draft is also the working
// state Save reads, so a refetch mid-session never drops it; a draft written
// before the view first loaded carries no fingerprint and is dropped then.
const BASE_CONFIG_FINGERPRINT_KEY = "baseConfigFingerprint";
// The latest server config this tab loaded, per view id.
const serverConfigFingerprints = new Map<string, string>();
// The server config the tab's current state of a view descends from.
const shownConfigFingerprints = new Map<string, string>();
// Views whose draft was cleared (saved, reset or created from) so the tab moves
// onto the server config the next load brings.
const viewsAwaitingOwnConfig = new Set<string>();

const fingerprintConfig = (config: unknown): string =>
  JSON.stringify(config ?? null);

const readRawTempView = (
  viewId: string
): Record<string, unknown> | undefined => {
  try {
    const viewStr = sessionStorage.getItem(`${VIEW_PREFIX}${viewId}`);
    return viewStr ? JSON.parse(viewStr) : undefined;
  } catch {
    return undefined;
  }
};

const writeTempView = (viewId: string, tempView: Record<string, unknown>) => {
  sessionStorage.setItem(
    `${VIEW_PREFIX}${viewId}`,
    JSON.stringify({
      ...tempView,
      [BASE_CONFIG_FINGERPRINT_KEY]:
        shownConfigFingerprints.get(viewId) ??
        serverConfigFingerprints.get(viewId)
    })
  );
};

const registerServerView = (view: ViewDataConfiguration) => {
  const fingerprint = fingerprintConfig(view.config);
  const previousFingerprint = serverConfigFingerprints.get(view.id);
  serverConfigFingerprints.set(view.id, fingerprint);

  if (previousFingerprint === undefined) {
    const tempView = readRawTempView(view.id);
    if (tempView && tempView[BASE_CONFIG_FINGERPRINT_KEY] !== fingerprint) {
      sessionStorage.removeItem(`${VIEW_PREFIX}${view.id}`);
    }
    shownConfigFingerprints.set(view.id, fingerprint);
  } else if (
    previousFingerprint !== fingerprint &&
    viewsAwaitingOwnConfig.has(view.id)
  ) {
    viewsAwaitingOwnConfig.delete(view.id);
    shownConfigFingerprints.set(view.id, fingerprint);
  }
};

// Overlay each view's temp view on the server copy, after recording the
// server config so a temp view left over from an older one is dropped.
export const applyTempViewsToServerViews = (
  serverViews: ViewDataConfiguration[]
): ViewDataConfiguration[] => {
  serverViews.forEach((view) => {
    if (view.id) {
      registerServerView(view);
    }
  });
  return serverViews.map((view) => {
    const tempView = getTempView(view);
    return tempView ? { ...view, config: tempView.config } : view;
  });
};

// Called when the tab starts showing a view, so later drafts are stamped with
// the server config that state came from rather than the latest one fetched.
export const recordActivatedViewConfig = (view: ViewDataConfiguration) => {
  if (!view.id) return;
  const latestFingerprint = serverConfigFingerprints.get(view.id);

  if (viewsAwaitingOwnConfig.has(view.id)) {
    if (fingerprintConfig(view.config) === latestFingerprint) {
      viewsAwaitingOwnConfig.delete(view.id);
      shownConfigFingerprints.set(view.id, latestFingerprint);
    } else {
      shownConfigFingerprints.delete(view.id);
    }
    return;
  }

  const draftFingerprint = readRawTempView(view.id)?.[
    BASE_CONFIG_FINGERPRINT_KEY
  ];
  const shownFingerprint =
    typeof draftFingerprint === "string" ? draftFingerprint : latestFingerprint;
  if (shownFingerprint === undefined) {
    shownConfigFingerprints.delete(view.id);
  } else {
    shownConfigFingerprints.set(view.id, shownFingerprint);
  }
};

export const saveConfigToTempView = (
  selectedView: ViewDataConfiguration | undefined,
  config: {
    filters?: Filters;
    sort?: SortQuery;
    groupBy?: string[];
    searchTerm?: string;
    columns?: ColumnData[];
  }
) => {
  if (!selectedView || selectedView.id === "") return;
  const tempViewKey = `${VIEW_PREFIX}${selectedView?.id}`;

  const existingViewStr = sessionStorage.getItem(tempViewKey);
  let existingConfig = {};

  if (existingViewStr) {
    try {
      const existingView = JSON.parse(existingViewStr);
      existingConfig = existingView.config || {};
    } catch (e) {
      console.error("Error parsing existing view config:", e);
    }
  }

  // An empty list is a table that hasn't laid out its columns yet (a view
  // switch clears them until they are reseeded from this draft), so it keeps
  // the draft's columns instead of replacing them.
  const { columns, ...restConfig } = config;
  const columnsToSave =
    columns && columns.length > 0 ? serializeColumnsForSave(columns) : undefined;

  writeTempView(selectedView.id, {
    ...selectedView,
    config: {
      ...existingConfig,
      ...restConfig,
      ...(columnsToSave !== undefined && { columns: columnsToSave })
    }
  });

  // Keep a type-keyed snapshot so the correct flex values are available
  // synchronously on next mount before selectedView.id is known from the API.
  if (selectedView.type && columnsToSave?.some((col) => col.flex != null)) {
    try {
      sessionStorage.setItem(
        `${FLEX_SNAPSHOT_PREFIX}${selectedView.type}`,
        JSON.stringify(columnsToSave)
      );
    } catch {
      // Quota exceeded or private mode — skip silently.
    }
  }
};

export const getTempView = (
  selectedView: ViewDataConfiguration | undefined
) => {
  if (!selectedView || selectedView.id === "") return;
  const tempViewKey = `${VIEW_PREFIX}${selectedView?.id}`;
  try {
    const viewStr = sessionStorage.getItem(tempViewKey);
    if (viewStr) {
      try {
        const tempView = JSON.parse(viewStr);
        // sessionStorage is a deserialization boundary exactly as a saved view
        // is: the config went through JSON.stringify, so dates come back as
        // strings, and a filter on a key no picker offers any more comes back
        // ready to keep narrowing with no chip to remove it. Normalized here
        // rather than at each restore site because none of them normalized.
        if (tempView?.config?.filters) {
          tempView.config.filters = normalizeFiltersAfterDeserialization(
            tempView.config.filters
          );
        }
        return tempView;
      } catch {
        sessionStorage.removeItem(tempViewKey);
      }
    }
  } catch {
    // Private mode or sandboxed iframe — sessionStorage.getItem may throw SecurityError.
  }
};

export const getViewColumns = (
  selectedView: ViewDataConfiguration | undefined,
  defaultColumns: ColumnData[]
): ColumnData[] => {
  if (!selectedView) {
    return defaultColumns;
  }

  const tempView = getTempView(selectedView);
  const savedColumns =
    tempView?.config?.columns || selectedView?.config?.columns;

  return mergeColumnsWithDefaults(savedColumns, defaultColumns);
};

// Re-seeds an empty columnsState from the same view+defaults merge the Display
// popover falls back to, so the picker and the table can't disagree about which
// columns exist, and appends flag-gated columns the state predates with their
// default visibility (visible) — a column the user deselected persists as
// isVisible:false, so it is matched here and stays hidden.
// MUST return currentColumns itself when nothing changes: useReconciledColumnsState
// gates its setState on reference identity, so a fresh array every call would loop.
export const reconcileColumnsStateWithDefaults = (
  currentColumns: ColumnData[],
  defaultColumns: ColumnData[],
  selectedView: ViewDataConfiguration | undefined,
  flagGatedColumnIds: readonly string[] = []
): ColumnData[] => {
  // Nothing to seed from before the defaults resolve, and merging into an empty
  // default set yields a new empty array every call — which would loop.
  if (defaultColumns.length === 0) {
    return currentColumns;
  }

  if (currentColumns.length === 0) {
    return getViewColumns(selectedView, defaultColumns);
  }

  const currentIds = new Set(currentColumns.map((column) => column.id));
  const missingColumns = flagGatedColumnIds
    .filter((columnId) => !currentIds.has(columnId))
    .map((columnId) => defaultColumns.find((column) => column.id === columnId))
    .filter((column): column is ColumnData => column !== undefined);

  return missingColumns.length
    ? [...currentColumns, ...missingColumns]
    : currentColumns;
};

// Round flex to 4dp — containerWidth ratio drifts slightly across reloads.
const roundFlex = (flex: number | undefined) =>
  flex != null && Number.isFinite(flex)
    ? Math.round(flex * 10000) / 10000
    : undefined;

// Compares only the columns both layouts have. A column the table gained after
// the view was saved (a new field, a flag turning on) is not an edit to the
// view, and neither is a saved column the table no longer has.
export const haveColumnsChangedFromSaved = (
  currentColumns: ColumnData[],
  savedColumns: ColumnData[]
): boolean => {
  const savedById = new Map(savedColumns.map((column) => [column.id, column]));
  const shared = currentColumns.filter((column) => savedById.has(column.id));
  const sharedIds = new Set(shared.map((column) => column.id));
  const idsByOrder = (columns: ColumnData[]) =>
    [...columns]
      .sort((first, second) => (first.order ?? 0) - (second.order ?? 0))
      .map((column) => column.id);

  if (
    !isEqual(
      idsByOrder(shared),
      idsByOrder(savedColumns.filter((column) => sharedIds.has(column.id)))
    )
  ) {
    return true;
  }

  return shared.some((column) => {
    const saved = savedById.get(column.id) as ColumnData;
    return (
      column.isVisible !== saved.isVisible ||
      roundFlex(column.flex) !== roundFlex(saved.flex)
    );
  });
};

// Save writes the table's whole current layout, so columns it gained since the
// view was last saved are recorded too. The draft and the view's own columns
// only stand in before the layout has loaded.
export const getColumnsToSave = (
  selectedView: ViewDataConfiguration | undefined,
  columnsState: ColumnData[]
): ColumnData[] => {
  if (columnsState.length > 0) {
    return serializeColumnsForSave(columnsState);
  }
  const tempView = selectedView ? getTempView(selectedView) : null;
  const columnsToSave =
    tempView?.config?.columns || selectedView?.config?.columns || columnsState;

  return serializeColumnsForSave(columnsToSave);
};

export const deleteTempView = (
  selectedView: ViewDataConfiguration | undefined
) => {
  if (!selectedView || selectedView.id === "") return;
  viewsAwaitingOwnConfig.add(selectedView.id);
  const tempViewKey = `${VIEW_PREFIX}${selectedView?.id}`;
  const tempView = sessionStorage.getItem(tempViewKey);
  if (tempView) {
    sessionStorage.setItem(tempViewKey, "");
    sessionStorage.removeItem(tempViewKey);
  }
};

export const updateTempViewColumnsState = (
  columnsState: ColumnData[],
  selectedView?: ViewDataConfiguration
) => {
  if (!selectedView || selectedView.id === "") return undefined;
  const tempView = getTempView(selectedView);
  try {
    const baseView = tempView || selectedView;
    writeTempView(selectedView.id, {
      ...baseView,
      config: {
        ...(baseView.config || {}),
        columns: columnsState
      }
    });
    return columnsState;
  } catch (e) {
    console.error("Error updating column state in view:", e);
  }
};

export const updateTempViewColumnsOrder = (
  newColumnOrder: string[],
  columnVisibility: VisibilityState,
  selectedView?: ViewDataConfiguration
) => {
  if (!selectedView || selectedView.id === "") return undefined;
  const tempView = getTempView(selectedView);
  try {
    const baseView = tempView || selectedView;
    let columns = baseView?.config?.columns || [];
    const hasExistingColumns = columns.length > 0;

    if (!hasExistingColumns) {
      if (!tempView) return undefined;
      const defaultColumns = newColumnOrder.map((id, index) => {
        const currentVisibility = columnVisibility?.[id];
        const isVisible =
          typeof currentVisibility === "boolean" ? currentVisibility : true;

        return {
          id: id,
          label: id,
          order: index,
          isVisible: isVisible
        };
      });

      columns = defaultColumns;
    }
    const columnsMap = columns.reduce(
      (map: Record<string, ColumnData>, col: ColumnData) => {
        map[col.id] = col;
        return map;
      },
      {}
    );

    const reorderedColumns = newColumnOrder
      .filter((id) => columnsMap[id])
      .map((id, index) => ({
        ...columnsMap[id],
        order: index
      }));

    writeTempView(selectedView.id, {
      ...baseView,
      config: {
        ...baseView.config,
        columns: reorderedColumns
      }
    });
    return reorderedColumns;
  } catch (e) {
    console.error("Error updating column order in view:", e);
  }
};

export const getPageNameFromTableId = (tableId: string) =>
  tableId.split("-")[0];

export const getColumnLabel = (
  columnId: string,
  pageName: string,
  t: TFunction<"translation", undefined>,
  arrMrrLabel?: string,
  arrOrMrrField?: string
) => {
  const columnLabelMap: Record<string, string | undefined> = {
    [arrOrMrrField ?? ""]: arrMrrLabel,
    createdAt: t("tables.createdAt"),
    statusChangedAt: t("tables.statusChangedAt"),
    alerts: t("deals.alerts.alerts")
  };

  if (arrOrMrrField && columnId === arrOrMrrField) {
    return arrMrrLabel;
  }

  return columnLabelMap[columnId] ?? t(`${pageName}.${columnId}`);
};

export const getDefaultColumnsState = (
  columns: string[],
  pageName: string,
  t: TFunction<"translation", undefined>,
  arrMrrLabel?: string,
  arrOrMrrField?: string
) => {
  const defaultState = columns.map((id: string, index: number) => {
    // `arrMrr` is a placeholder for whichever of ARR/MRR the account is set to,
    // and `arrMrrLocal` for the terms-currency half of that same pair.
    let columnId = id;
    if (arrOrMrrField) {
      if (id === "arrMrr") columnId = arrOrMrrField;
      else if (id === "arrMrrLocal") columnId = termsFieldName(arrOrMrrField);
    }

    return {
      id: columnId,
      label: getColumnLabel(columnId, pageName, t, arrMrrLabel, arrOrMrrField),
      isVisible: true,
      order: index
    } as ColumnData;
  });

  return defaultState;
};

// ARR/MRR/CARR/CMRR are managed as dynamic hardcoded columns (toggled via isArr
// account setting) and must never appear as DB-driven custom columns regardless
// of show_in_detailed_view. CRM-2952 will eventually set the inactive variant to
// show_in_detailed_view=False, but until that migration runs we exclude them here.
// The terms half of each is excluded on the same grounds: the built-in column
// pair owns both keys, so a registry-driven copy would render the same figure
// under a second checkbox.
const ARR_MRR_METRIC_KEYS = ["arr", "mrr", "carr", "cmrr"] as const;

// Field keys arrive from the entity-fields endpoint camelCased, so the terms
// half is spelled by the shared helper rather than written out a second time —
// a snake_case entry matches nothing and silently re-admits the column.
const ARR_MRR_SYSTEM_FIELD_KEYS = new Set<string>([
  ...ARR_MRR_METRIC_KEYS,
  ...ARR_MRR_METRIC_KEYS.map(termsFieldName)
]);

// ICP-fit fields are pushed by the backend entity-fields endpoint but must stay
// hidden from table columns and filters until the ICP feature flag is enabled,
// mirroring the detail-view gating. Derived from the canonical key lists in
// icpFit/helpers so there is a single source of truth for "which key is ICP fit".
export const LEAD_ICP_FIT_FIELD_KEYS = new Set<string>(ICP_FIT_LEAD_KEYS);
export const ICP_FIT_FIELD_KEYS = new Set<string>(ICP_FIT_PEOPLE_COMPANY_KEYS);

// Removes fields whose camelCased key is in `hiddenFieldKeys` from the list of
// backend-driven fields, so they surface in neither columns nor filters.
export const excludeFieldsByKey = (
  fields: APIFieldItem[],
  hiddenFieldKeys: Set<string>
): APIFieldItem[] =>
  fields.filter(
    (field) => !hiddenFieldKeys.has(camelizeFieldKey(field.fieldKey))
  );

const PARTNER_FIELD_DISPLAY_TYPES: ReadonlySet<string> = new Set([
  FieldDisplayType.PARTNERS,
  FieldDisplayType.PARTNER_CONTACTS,
  // Raw backend values for fields created before the PARTNER_CONTACTS
  // displayType was renamed from "partnerContacts" to "partnersContacts" —
  // legacy field rows can still carry these, so exclusion must match on the
  // raw string too.
  "partner_contacts",
  "partners_contacts",
  "partnerContacts"
]);

export const excludePartnerFields = (fields: APIFieldItem[]): APIFieldItem[] =>
  fields.filter((field) => !PARTNER_FIELD_DISPLAY_TYPES.has(field.displayType));

export const filterCustomFieldsForColumnManagement = (
  fields: APIFieldItem[] | undefined,
  systemColumnIds?: Set<string>,
  // A custom or system field hidden from the detailed view gets no column
  // unless the table opts in. Filter and Group By options follow the table's
  // columns.
  includeHiddenCustomFields = false
): APIFieldItem[] => {
  if (!fields) return [];

  // SYSTEM-type fields come from the backend's entity-fields endpoint. Scalar
  // display types (date, number, currency, text, single-select, …) render
  // through the generic custom-column pipeline and export their formatted value,
  // so they are safe to surface in the picker. Entity-reference display types
  // hold an unresolved FK id that the generic renderer only humanizes when the
  // row carries a `<field>_resolved` value, so they stay restricted to fields
  // that map to a known hardcoded system column id for this table — otherwise
  // the picker exposes a column whose CSV export emits raw IDs.
  const isAllowedSystemField = (field: APIFieldItem): boolean => {
    if (ARR_MRR_SYSTEM_FIELD_KEYS.has(field.fieldKey)) return false;
    if (!isEntityDisplayType(field.displayType)) return true;
    // Partner fields are entity-reference SYSTEM fields, gated behind
    // enablePartnerships upstream. Unlike other entity references, the deals/
    // leads list endpoint resolves partner/partnerContact to a
    // `<field>_resolved` display value, so the generic column pipeline renders
    // the partner name (and CSV export folds the same value) without a
    // hardcoded ColumnDef — surface them in the picker even though they map to
    // no hardcoded system column.
    if (PARTNER_FIELD_DISPLAY_TYPES.has(field.displayType)) return true;
    if (!systemColumnIds) return true;
    return systemColumnIds.has(camelizeFieldKey(field.fieldKey));
  };

  // SCO KPIs are surfaced as built-in columns, so SCO (like HEADER) fields are
  // not offered here. A button holds no value to show in a column.
  return fields.filter(
    (field) =>
      field.displayType !== FieldDisplayType.BUTTON &&
      ((field.fieldType === SettingsFieldType.CUSTOM &&
        (includeHiddenCustomFields || field.showInDetailedView)) ||
        field.fieldType === SettingsFieldType.FORMULA ||
        field.fieldType === SettingsFieldType.LINKED ||
        (field.fieldType === SettingsFieldType.SYSTEM &&
          (includeHiddenCustomFields || field.showInDetailedView) &&
          isAllowedSystemField(field)))
  );
};

export const convertAPIFieldToColumnData = (
  field: APIFieldItem,
  index: number
): ColumnData => {
  return {
    id: camelizeFieldKey(field.fieldKey),
    label: field.label,
    isVisible: false,
    order: index
  };
};

/**
 * Fold columns added since a layout was saved into that saved layout.
 *
 * Seeding only ever fills an empty state, so without this an account that
 * already has a saved layout would never be offered a column introduced after
 * it saved. Each absent default is spliced in after the nearest preceding
 * column of the default order that the saved layout still has, so it lands
 * where it belongs rather than at the end. Saved columns keep their own
 * visibility and order untouched; the same array is returned when nothing is
 * missing, so callers can use it directly in a state updater without
 * re-rendering.
 */
export const mergeMissingDefaultColumns = (
  savedColumns: ColumnData[],
  defaultColumns: ColumnData[]
): ColumnData[] => {
  if (savedColumns.length === 0 || defaultColumns.length === 0) {
    return savedColumns;
  }
  const existingIds = new Set(savedColumns.map((col) => col.id));
  const missing = defaultColumns.filter(
    (col) => col.isVisible && !existingIds.has(col.id)
  );
  if (missing.length === 0) return savedColumns;

  const defaultOrder = defaultColumns.map((col) => col.id);
  const result = [...savedColumns];
  for (const missingColumn of missing) {
    const defaultIdx = defaultOrder.indexOf(missingColumn.id);
    let insertAt = 0;
    for (let i = defaultIdx - 1; i >= 0; i--) {
      const pos = result.findIndex((col) => col.id === defaultOrder[i]);
      if (pos !== -1) {
        insertAt = pos + 1;
        break;
      }
    }
    result.splice(insertAt, 0, missingColumn);
  }
  return result;
};

export const getDefaultColumnsStateWithCustomFields = (
  systemColumns: string[],
  customFields: APIFieldItem[] | undefined,
  pageName: string,
  t: TFunction<"translation", undefined>,
  arrMrrLabel?: string,
  arrOrMrrField?: string,
  // Column ids that start unchecked in Columns management regardless of how they
  // reached the table. Applied last, over both the system defaults and the
  // registry's show_in_detailed_view, so a single caller-supplied rule covers a
  // built-in column and its registry-driven twin alike. This is only a starting
  // point — a saved columnsState still wins.
  hiddenByDefaultColumnIds?: ReadonlySet<string>,
  // Picker labels for system columns whose table header is composed at render
  // time rather than translated — the split money columns carry a currency code
  // or basis in their header, and the checkbox has to say the same thing or the
  // two halves of a pair are indistinguishable in Columns management.
  labelOverrides?: ReadonlyMap<string, string>,
  // See `filterCustomFieldsForColumnManagement`.
  includeHiddenCustomFields = false
): ColumnData[] => {
  // A custom/linked field that is currently visible arrives in `systemColumns`
  // (it was materialized as a table column), so getDefaultColumnsState has no
  // translation key for its account-specific field key. Fall back to the
  // field's own label, same priority tWithFallback applies to cell values
  // elsewhere — a real translation still wins if one ever exists.
  const customFieldLabelByColumnId = new Map(
    (customFields ?? []).map((field) => [
      camelizeFieldKey(field.fieldKey),
      field.label
    ])
  );
  const systemColumnsState = getDefaultColumnsState(
    systemColumns,
    pageName,
    t,
    arrMrrLabel,
    arrOrMrrField
  ).map((column) => {
    const labelOverride = labelOverrides?.get(column.id);
    if (labelOverride) return { ...column, label: labelOverride };
    const customFieldLabel = customFieldLabelByColumnId.get(column.id);
    if (!customFieldLabel) return column;
    // getColumnLabel echoes `${pageName}.${id}` when it has no translation.
    // Anything else (including a shared `tables.*` key the grid header also
    // uses) is the on-screen label and wins over the registry's stored one.
    const hasTranslatedLabel =
      !!column.label && column.label !== `${pageName}.${column.id}`;
    return hasTranslatedLabel ? column : { ...column, label: customFieldLabel };
  });

  const systemColumnIds = new Set(systemColumnsState.map((col) => col.id));

  const filteredCustomFields = filterCustomFieldsForColumnManagement(
    customFields,
    systemColumnIds,
    includeHiddenCustomFields
  ).filter((field) => !systemColumnIds.has(camelizeFieldKey(field.fieldKey)));

  // The override reaches registry-driven columns too: the split money pair the
  // account's registry carries (CMRR on deals) is not a system column, and a
  // checkbox reading the stored label while the grid header carries the
  // currency code leaves the two halves indistinguishable in the picker.
  const customColumnsState = filteredCustomFields.map((field, index) => {
    const columnData = convertAPIFieldToColumnData(
      field,
      systemColumnsState.length + index
    );
    const labelOverride = labelOverrides?.get(columnData.id);
    return labelOverride ? { ...columnData, label: labelOverride } : columnData;
  });

  const columnsState = [...systemColumnsState, ...customColumnsState];
  if (!hiddenByDefaultColumnIds?.size) return columnsState;
  return columnsState.map((column) =>
    hiddenByDefaultColumnIds.has(column.id)
      ? { ...column, isVisible: false }
      : column
  );
};

// Flip the named columns to visible, by column id.
//
// Registry-driven columns arrive unchecked (convertAPIFieldToColumnData), so a
// column that should be ticked out of the box needs promoting. ServicesTable and
// ContractsTable promote by field type instead — SYSTEM, FORMULA, or LINKED with
// show_in_detailed_view — which is the general rule and the better one where it
// applies. It does not apply on leads: every LINKED lead field the table already
// renders as a hardcoded column (company_name, contact_email, product_name, and
// the ICP pair) carries show_in_detailed_view=FALSE, because on leads that flag
// only means "not a row in the Details list". Promoting by field type there would
// tick three unrelated columns (company_state_region, company_industry_id,
// company_sub_industry_id) and still miss the two that need it. Hence an explicit
// id list rather than a rule.
//
// Applied both to a freshly built default state and to the columns a saved view
// carries, since a view saved while a column was still seeding unchecked stores
// that hidden value and no default can reach it.
export const withColumnsVisible = (
  columns: ColumnData[],
  visibleColumnIds: ReadonlySet<string>
): ColumnData[] => {
  if (visibleColumnIds.size === 0) return columns;
  const needsChange = columns.some(
    (column) => visibleColumnIds.has(column.id) && !column.isVisible
  );
  // Same array back when there is nothing to do, so callers can hand the result
  // straight to a state updater without forcing a render.
  if (!needsChange) return columns;
  return columns.map((column) =>
    visibleColumnIds.has(column.id) && !column.isVisible
      ? { ...column, isVisible: true }
      : column
  );
};

export const parseMultiSelectValue = (value: unknown): string[] => {
  if (value === null || value === undefined || value === "") {
    return [];
  }

  if (Array.isArray(value)) {
    return value.map(String);
  }

  const stringValue = String(value);
  if (stringValue.startsWith("[") && stringValue.endsWith("]")) {
    try {
      const jsonString = stringValue.replace(/'/g, '"');
      const parsed = JSON.parse(jsonString);
      if (Array.isArray(parsed)) {
        return parsed.map(String);
      }
    } catch {
      const inner = stringValue.slice(1, -1);
      const items = inner
        .split(",")
        .map((item) => item.trim().replace(/^['"]|['"]$/g, ""))
        .filter(Boolean);
      if (items.length > 0) {
        return items;
      }
    }
  }

  return [stringValue];
};

export const formatCustomFieldValue = (
  value: unknown,
  displayType: string | undefined
): string | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (displayType === FieldDisplayType.MULTI_SELECT) {
    const items = parseMultiSelectValue(value);
    return items.length > 0 ? items.join(", ") : null;
  }

  return stripHtml(String(value));
};

interface DateRangeValue {
  fromDate?: string;
  toDate?: string;
}

/** A date-range field's value, stored as a JSON string or already an object. */
const parseDateRangeValue = (rawValue: unknown): DateRangeValue | null => {
  if (typeof rawValue !== "string") {
    return (rawValue as DateRangeValue | null | undefined) ?? null;
  }
  try {
    return JSON.parse(rawValue) as DateRangeValue | null;
  } catch {
    return null;
  }
};

/** A date range groups by its start date, which is also what it sorts by. */
const dateRangeGroupDate = (rawValue: unknown): string | null =>
  parseDateRangeValue(rawValue)?.fromDate ?? null;

/**
 * How a custom field's value buckets by date: a date field by its value, a date range by
 * its start date, and undefined for a field that holds no date.
 */
export const customFieldDateGrouping = (
  field: APIFieldItem
): { groupDateOf?: (rawValue: unknown) => string | null } | undefined => {
  if (field.displayType === FieldDisplayType.DATE) return {};
  if (field.displayType === FieldDisplayType.DATE_RANGE) {
    return { groupDateOf: dateRangeGroupDate };
  }
  return undefined;
};

/**
 * A currency field's amounts are all in the code it is configured with; one with none
 * configured shows "$", so its amounts group under that.
 */
const configuredCurrencyGroupCode = (
  field: APIFieldItem
): CurrencyGroupCode => {
  const currencyCode =
    (field.config as { defaultValue?: string } | null)?.defaultValue || "$";
  return () => currencyCode;
};

export const mapSelectedCustomColumnsToTableColumns = <TRow = unknown,>(
  selectedColumns: ColumnData[],
  customFields: APIFieldItem[] | undefined,
  t: TFunction<"translation", undefined>,
  systemColumnIds?: Set<string>,
  groupBy?: string[],
  // Per-column cell renderer for CURRENCY-type linked fields whose config has
  // no static currency (e.g. a contract's terms currency, which varies row by
  // row) — the generic `${symbol}${value}` template below can only express a
  // single configured currency. Callers supply their own currency-aware cell
  // (e.g. TermsCurrencyCell) rather than this shared helper importing one
  // directly, so this cross-table module stays free of any single feature
  // page's components. Keyed by column id (the camelCased field key).
  currencyFieldResolvers?: Record<
    string,
    (amount: number, row: TRow) => ReactNode
  >,
  // Header text for registry-driven columns whose label is composed at render
  // time rather than stored — the system half of a split money pair gains the
  // account's currency code, which the registry cannot hold because it is one
  // row shared by every account. Keyed by column id, same as the resolvers.
  columnLabelOverrides?: ReadonlyMap<string, string>,
  // Column ids whose values are not comparable across rows, so the header must
  // offer no sort at all — see the `enableSorting` expression below.
  unsortableColumnIds?: ReadonlySet<string>,
  // The currency a CURRENCY column's amount is in, for columns whose currency the
  // field config does not fix — the same columns `currencyFieldResolvers` renders.
  // Grouping by such a column groups by this code. Keyed by column id.
  currencyCodeResolvers?: Record<string, CurrencyGroupCode>,
  // See `filterCustomFieldsForColumnManagement`.
  includeHiddenCustomFields = false
) => {
  if (!customFields) return [];

  const nonSystemFields = filterCustomFieldsForColumnManagement(
    customFields,
    systemColumnIds,
    includeHiddenCustomFields
  ).filter((field) => !systemColumnIds?.has(camelizeFieldKey(field.fieldKey)));

  const customFieldsMap = keyBy(nonSystemFields, (field) =>
    camelizeFieldKey(field.fieldKey)
  );

  const notAvailable = t("tables.notAvailable");
  const isEmpty = (value: unknown): boolean =>
    value === null || value === undefined || value === "";

  // A hidden custom field that is the current grouping target must still
  // produce a ColumnDef: TanStack groups by the column and the group-band
  // header resolves its label via the column's cell renderer. Without the def
  // the band renders blank (linked/system fields are unaffected — their defs
  // always exist and are hidden via columnVisibility). buildVisibilityState
  // still keeps the column out of the visible set from its isVisible: false.
  const groupedIds = new Set(groupBy ?? []);
  return selectedColumns
    .filter(
      (column) =>
        customFieldsMap[column.id] &&
        (column.isVisible || groupedIds.has(column.id))
    )
    .map((column) => {
      const field = customFieldsMap[column.id];
      const singleSelectLabelById =
        field.displayType === FieldDisplayType.SINGLE_SELECT
          ? new Map(
              parseOptions(field.config).map((option) => [
                String(option.id),
                option.displayName
              ])
            )
          : null;
      return {
        accessorKey: column.id,
        ...(field.displayType === FieldDisplayType.CURRENCY
          ? // Numeric comparator; the default "text" sort mis-orders negatives.
            { sortingFn: monetarySortingFn }
          : {}),
        header: columnLabelOverrides?.get(column.id) ?? field.label,
        cell: (info: any) => {
          const resolvedKey = `${column.id}_resolved`;
          const rawValue = info.row.original[resolvedKey] ?? info.getValue();

          if (field.displayType === FieldDisplayType.MULTI_SELECT) {
            const values = parseMultiSelectValue(rawValue);
            return values.length > 0 ? (
              <MultiSelectBadgesCell values={values} />
            ) : (
              notAvailable
            );
          }

          // A checklist item is boolean, so an unset value means "not
          // completed" rather than N/A — resolve it before the isEmpty guard.
          if (field.displayType === FieldDisplayType.CHECKLIST_ITEM) {
            return rawValue === "checked" || rawValue === true
              ? t("completed")
              : t("notCompleted");
          }

          // ICP fit fields (people/companies `icpFit`, leads `companyIcpFit`/
          // `contactIcpFit`) carry an enum value but no `config.options`; render
          // the score bars + translated label instead of the raw number. Gate
          // on the accessor itself rather than `rawValue`/isEmpty below: these
          // point at a lookup table, so the API also sends a `_resolved`
          // display label ("Strong") that wins the `??` above — Number("Strong")
          // is NaN, and an empty-string `_resolved` trips the isEmpty guard —
          // either way masking a real score on the accessor.
          if (isIcpFitFieldKey(column.id)) {
            const accessorValue = info.getValue();
            const icpFitValue = Number(accessorValue);
            return isEmpty(accessorValue) || Number.isNaN(icpFitValue) ? (
              notAvailable
            ) : (
              <IcpFitValue value={icpFitValue} compact />
            );
          }

          if (isEmpty(rawValue)) {
            return notAvailable;
          }

          switch (field.displayType) {
            case FieldDisplayType.NUMBER:
            case FieldDisplayType.CURRENCY:
            case FieldDisplayType.PERCENTAGE: {
              const numValue = parseFloat(String(rawValue));
              if (isNaN(numValue)) {
                return notAvailable;
              }

              if (field.displayType === FieldDisplayType.PERCENTAGE) {
                // Stored as a fraction (0.3); display in 1–100 space.
                const { value } = formatNumber(numValue * 100, 2, false, false);
                return `${value}%`;
              }

              if (field.displayType === FieldDisplayType.CURRENCY) {
                const currencyCellRenderer =
                  currencyFieldResolvers?.[column.id];
                if (currencyCellRenderer) {
                  return currencyCellRenderer(numValue, info.row.original);
                }
                const { value } = formatNumber(numValue, 2, false, false);
                const currencySymbol =
                  getConfiguredCurrencySymbol(field.config) ?? "$";
                return `${currencySymbol}${value}`;
              }

              const { value } = formatNumber(numValue, 2, false, false);
              return value;
            }

            case FieldDisplayType.DATE:
              return simpleDate(toLocalDate(rawValue as string), "short");

            case FieldDisplayType.DATE_RANGE: {
              const dateRangeValue = parseDateRangeValue(rawValue);
              if (!dateRangeValue?.fromDate && !dateRangeValue?.toDate) {
                return notAvailable;
              }
              const fromDateFormatted = dateRangeValue.fromDate
                ? simpleDate(toLocalDate(dateRangeValue.fromDate), "short")
                : "";
              const toDateFormatted = dateRangeValue.toDate
                ? simpleDate(toLocalDate(dateRangeValue.toDate), "short")
                : "";
              if (fromDateFormatted && toDateFormatted) {
                return `${fromDateFormatted} - ${toDateFormatted}`;
              }
              return fromDateFormatted || toDateFormatted;
            }

            case FieldDisplayType.URL:
              return <UrlCell url={String(rawValue)} />;

            case FieldDisplayType.CHECKBOX: {
              const formattedValue = formatCustomFieldValue(
                rawValue,
                field.displayType
              );
              if (formattedValue && field?.config?.checkboxInlineText) {
                return (
                  <div className="flex gap-2 items-center">
                    <Text>{formattedValue}</Text>
                    <Text variant="bodyTertiary">
                      {field?.config?.checkboxInlineText}
                    </Text>
                  </div>
                );
              }
              return formattedValue || notAvailable;
            }

            case FieldDisplayType.SINGLE_SELECT: {
              return (
                singleSelectLabelById?.get(String(rawValue)) ?? String(rawValue)
              );
            }

            // A switch stores the key of its option; show the option's label.
            case FieldDisplayType.SWITCH: {
              const switchLabels: Record<string, string> = createSwitchOptions(
                field.config,
                t
              );
              const key = String(rawValue);
              return Object.prototype.hasOwnProperty.call(switchLabels, key)
                ? switchLabels[key]
                : key;
            }

            case FieldDisplayType.FILE_UPLOAD: {
              const attachments = parseFileFieldValue(rawValue);
              if (attachments.length === 0) {
                return notAvailable;
              }
              return (
                <MultiSelectBadgesCell
                  values={attachments.map((attachment) => attachment.filename)}
                />
              );
            }

            default: {
              const formattedValue = formatCustomFieldValue(
                rawValue,
                field.displayType
              );
              return formattedValue || notAvailable;
            }
          }
        },
        // A file field's value is a JSON list of attachments — there is no
        // meaningful order to sort it by. Neither has the terms half of a split
        // money pair: each row carries its own currency, so ordering by the raw
        // figure interleaves them. A date range sorts by its start date, on the
        // server. `sortingFn` above cannot express any of that — it is a
        // client-side comparator, and every table using this helper hands
        // sorting to the API via `manualSorting`, which leaves it inert.
        enableSorting:
          field.displayType !== FieldDisplayType.FILE_UPLOAD &&
          !unsortableColumnIds?.has(column.id),
        minSize: field.displayType === FieldDisplayType.CHECKBOX ? 50 : 100,
        meta: {
          disableTooltip: field.displayType === FieldDisplayType.URL,
          groupByMonth: !!customFieldDateGrouping(field),
          ...customFieldDateGrouping(field),
          // File fields are not offered as a grouping; a saved view grouped by
          // one still groups by whether the field holds a file.
          groupByFilePresence:
            field.displayType === FieldDisplayType.FILE_UPLOAD,
          ...(field.displayType === FieldDisplayType.CURRENCY
            ? {
                currencyGroupCode:
                  currencyCodeResolvers?.[column.id] ??
                  configuredCurrencyGroupCode(field)
              }
            : {})
        }
      };
    });
};

// Shared functions for ColumnsManagement components

export const createColumnLabelResolver = (
  tableId: string,
  t: TFunction<"translation", undefined>,
  arrMrrLabel: string,
  arrMrrField: string,
  customFields?: APIFieldItem[],
  // Labels composed at render time (currency code, basis) for a column whose
  // registry row either doesn't exist (e.g. services' pricing has no
  // revenue_items_fields row) or predates the relabel migration — the bare
  // i18n translation below has no way to express that suffix, so it must not
  // be allowed to win over a caller-supplied composed label. Callers pass the
  // same map they used to build defaultColumnsState, keyed by column id.
  labelOverrides?: ReadonlyMap<string, string>
) => {
  return (columnId: string, originalLabel: string): string => {
    // Checked before customFields: a column id can collide with an unrelated
    // registry row of the same name (a deal's own `total_contract_value`
    // system row vs. the contracts column keyed `contracts_total_contract_value`),
    // and that row's stored label would otherwise replace the composed one.
    // The map is built from defaultColumnsState in the same render that
    // supplies customFields, so it cannot be the staler of the two.
    const labelOverride = labelOverrides?.get(columnId);
    if (labelOverride) return labelOverride;

    if (customFields) {
      const customField = customFields.find(
        (field) => camelizeFieldKey(field.fieldKey) === columnId
      );
      if (customField) {
        return customField.label;
      }
    }

    const pageName = getPageNameFromTableId(tableId);
    const systemLabel = getColumnLabel(
      columnId,
      pageName,
      t,
      arrMrrLabel,
      arrMrrField
    );
    // getColumnLabel echoes `${pageName}.${columnId}` for an id with no
    // translation entry — an account-specific custom/linked field key whose
    // customFields entry has not loaded yet (the fields query refetches right
    // after a field is created). Prefer the column's own saved label over that
    // raw path instead of leaking it.
    if (systemLabel === `${pageName}.${columnId}`) {
      return originalLabel;
    }
    return systemLabel || originalLabel;
  };
};

export const initializeColumnsWithLabels = (
  savedColumns: ColumnData[] | undefined,
  defaultColumns: ColumnData[],
  labelResolver: (columnId: string, originalLabel: string) => string,
  useCustomFieldsMerging: boolean = false
): ColumnData[] => {
  if (savedColumns && savedColumns.length > 0) {
    if (useCustomFieldsMerging) {
      // Merge saved columns with defaultColumns to ensure custom fields are included
      const savedColumnsMap = keyBy(savedColumns, "id");

      return defaultColumns.map((defaultColumn) => {
        const savedColumn = savedColumnsMap[defaultColumn.id];
        if (savedColumn) {
          return {
            ...savedColumn,
            label: labelResolver(savedColumn.id, savedColumn.label)
          };
        } else {
          return defaultColumn;
        }
      });
    } else {
      return savedColumns.map((column) => ({
        ...column,
        label: labelResolver(column.id, column.label)
      }));
    }
  } else {
    return defaultColumns.map((column) => ({
      ...column,
      label: labelResolver(column.id, column.label)
    }));
  }
};

export const createDragHandlers = () => {
  const handleDragUpdate = (
    update: any,
    setDragState: (state: {
      isDragging: boolean;
      destinationIndex: number | null;
    }) => void
  ) => {
    if (!update.destination) {
      setDragState({ isDragging: true, destinationIndex: null });
      return;
    }
    setDragState({
      isDragging: true,
      destinationIndex: update.destination.index
    });
  };

  const handleDragEnd = (
    result: any,
    setDragState: (state: {
      isDragging: boolean;
      destinationIndex: number | null;
    }) => void,
    setColumnStates: (updater: (prev: ColumnData[]) => ColumnData[]) => void
  ) => {
    setDragState({ isDragging: false, destinationIndex: null });
    const { destination, source } = result;

    if (!destination) return;
    if (destination.index === source.index) return;

    setColumnStates((currentColumns) => {
      const newColumns = [...currentColumns];
      const [removed] = newColumns.splice(source.index, 1);
      newColumns.splice(destination.index, 0, removed);
      return newColumns.map((column, index) => ({
        ...column,
        order: index
      }));
    });
  };

  return { handleDragUpdate, handleDragEnd };
};

const getFilterTypeForCustomField = (
  field: APIFieldItem
): FilterValue | null => {
  // ICP fit is a fixed enum filtered via a dedicated page-level `enum` filter
  // (see getIcpFitFilterOption). Exclude it from the generic custom-field path
  // so it isn't also surfaced here as an empty single-select (no config.options).
  if (isIcpFitFieldKey(field.fieldKey)) {
    return null;
  }

  // Products/Licenses are linked reference fields but are not modeled as full
  // CRM entities — there is no EntityType member, no getEntityTypeFromDisplayType
  // case, and no server-search hook for them. Routing them through the entity
  // branch below yields a null EntityType, which drops the field from filter and
  // group-by pickers entirely. Resolve them to a text filter (the same fallback
  // used for linked fields that reference a plain column) so they surface.
  if (
    isProductsDisplayType(field.displayType) ||
    isLicensesDisplayType(field.displayType)
  ) {
    return { type: "text" };
  }

  if (field.displayType && isEntityDisplayType(field.displayType)) {
    const entityType = getEntityTypeFromDisplayType(field.displayType);
    if (!entityType) return null;
    const showAvatar =
      field.displayType === FieldDisplayType.USERS ||
      field.displayType === FieldDisplayType.PEOPLE;
    return {
      type: "map",
      values: [],
      showAvatar,
      sort: true,
      showSearch: true,
      entity: entityType
    };
  }

  switch (field.displayType) {
    case FieldDisplayType.SINGLE_SELECT:
    case FieldDisplayType.MULTI_SELECT:
      // Handle options from config - use custom field type
      if (field.config?.options) {
        return {
          type: "customField",
          values: {
            field,
            options: field.config.options
          }
        };
      }
      return { type: "customField", values: { field, options: {} } };

    case FieldDisplayType.NUMBER:
      return { type: "numeric" };

    case FieldDisplayType.PERCENTAGE:
      return { type: "numeric", isPercentage: true };

    case FieldDisplayType.CURRENCY:
      return {
        type: "numeric",
        isMonetary: true,
        currencySymbol: getConfiguredCurrencySymbol(field.config)
      };

    case FieldDisplayType.DATE:
    case FieldDisplayType.DATE_RANGE:
      return { type: "date" };

    // A switch stores the key of the option it is set to, so it is filtered by
    // its options rather than as yes/no.
    case FieldDisplayType.SWITCH:
      return {
        type: "customField",
        values: {
          field,
          options: createSwitchOptions(field.config, (key) => i18n.t(key))
        }
      };

    case FieldDisplayType.CHECKBOX:
      return {
        type: "boolean",
        label: field.label
      };

    case FieldDisplayType.TEXT:
    case FieldDisplayType.TEXTAREA:
    case FieldDisplayType.EMAIL:
    case FieldDisplayType.URL:
      return { type: "text" };

    // No options to pick from, so the filter offers only "No value" and its
    // negation: a field without a file, or with at least one.
    case FieldDisplayType.FILE_UPLOAD:
      return { type: "map", values: [] };

    case FieldDisplayType.BUTTON:
      return null;

    default:
      // LINKED fields without an entity displayType reference a regular column
      // on another table — filter them as text.
      if (field.fieldType === SettingsFieldType.LINKED) {
        return { type: "text" };
      }
      return null;
  }
};

export const filterFieldsForFilterOptions = (
  fields: APIFieldItem[] | undefined
): APIFieldItem[] => {
  if (!fields) return [];
  return fields.filter((field) => {
    if (field.displayType === FieldDisplayType.BUTTON) return false;
    // Offered only through its column, and
    // `filterCustomFieldsForColumnManagement` gives a field hidden from the
    // detailed view a column only on a table that opts in.
    if (field.fieldType === SettingsFieldType.CUSTOM) return true;
    if (field.fieldType === SettingsFieldType.FORMULA) return true;
    if (field.fieldType === SettingsFieldType.LINKED) return true;
    if (field.fieldType === SettingsFieldType.SYSTEM) {
      const filterType = getFilterTypeForCustomField(field);
      return filterType !== null;
    }
    return false;
  });
};

// Helper function to convert custom fields to filter options.
//
// Iterates `tableColumns` so that filters follow column visibility/order for
// CUSTOM/FORMULA/LINKED fields. SYSTEM fields are a special case: the column
// manager gates out SYSTEM fields lacking a matching ColumnDef to suppress
// unrenderable columns. The filter picker must NOT inherit that gate — SYSTEM
// fields like the People-typed `contactId` on Leads still have a working
// filter UI even without a column. We append those fields after the
// column-driven set.
export const mapCustomFieldsToFilterOptions = (
  tableColumns: ColumnData[],
  customFields: APIFieldItem[] | undefined
): DataTableFilter[] => {
  if (!customFields) return [];

  const filterableFields = excludeFieldsByKey(
    filterFieldsForFilterOptions(customFields),
    NON_FILTERABLE_CONTRACT_ROLLUP_FIELD_KEYS
  );
  const customFieldsMap = keyBy(filterableFields, (field) =>
    camelizeFieldKey(field.fieldKey)
  );

  const fromColumns = tableColumns
    .filter((column) => customFieldsMap[column.id])
    .map((column): DataTableFilter | null => {
      const field = customFieldsMap[column.id];
      const filterType = getFilterTypeForCustomField(field);
      if (!filterType) return null;
      return {
        key: column.id, // Use camelCase field key for internal operations
        filterType,
        customFieldLabel: field.label, // Store the label for all custom field types
        displayType: field.displayType
      };
    })
    .filter((filter): filter is DataTableFilter => filter !== null);

  const columnKeys = new Set(fromColumns.map((f) => f.key));
  const systemOnlyFilters = filterableFields
    .filter(
      (field) =>
        field.fieldType === SettingsFieldType.SYSTEM &&
        !columnKeys.has(camelizeFieldKey(field.fieldKey))
    )
    .map((field): DataTableFilter | null => {
      const filterType = getFilterTypeForCustomField(field);
      if (!filterType) return null;
      return {
        key: camelizeFieldKey(field.fieldKey),
        filterType,
        customFieldLabel: field.label,
        displayType: field.displayType
      };
    })
    .filter((filter): filter is DataTableFilter => filter !== null);

  return [...fromColumns, ...systemOnlyFilters];
};

export const NON_GROUPABLE_DISPLAY_TYPES: ReadonlySet<FieldDisplayType> =
  new Set([
    FieldDisplayType.TEXT,
    FieldDisplayType.TEXTAREA,
    FieldDisplayType.NUMBER,
    FieldDisplayType.PERCENTAGE,
    FieldDisplayType.EMAIL,
    FieldDisplayType.URL,
    FieldDisplayType.BUTTON,
    FieldDisplayType.FILE_UPLOAD
  ]);

/**
 * Groupable in the table but not by the API: the server groups by exact value, while
 * the table computes each row's group itself — the month, quarter or year a date (or a
 * date range's start) falls in, or the currency an amount is in.
 */
const CLIENT_ONLY_GROUPABLE_DISPLAY_TYPES: ReadonlySet<FieldDisplayType> =
  new Set([
    FieldDisplayType.DATE,
    FieldDisplayType.DATE_RANGE,
    FieldDisplayType.CURRENCY
  ]);

export interface GroupByOption {
  id: string;
  displayName: string;
}

/**
 * Only a stored column can be grouped by the API — a custom field's values live
 * in their own table and a linked, SCO or formula field is resolved per row, so
 * asking the server to group by one is a request it cannot answer.
 */
const SERVER_GROUPABLE_FIELD_TYPES: ReadonlySet<SettingsFieldType> = new Set([
  SettingsFieldType.SYSTEM,
  SettingsFieldType.EDITABLE_SYSTEM
]);

/**
 * The stored column behind a group option, or undefined when the option is not
 * one the server can group by. A column showing a resolved name (`productName`)
 * falls back to the id field it comes from (`productId`) — the same pairing the
 * group-by options themselves are built on.
 */
/** The stored column a group option reaches, and what that column holds. */
export interface ServerGroupField {
  fieldKey: string;
  displayType: FieldDisplayType;
}

export const resolveServerGroupField = (
  groupField: string | undefined,
  fields: APIFieldItem[] | undefined
): ServerGroupField | undefined => {
  if (!groupField || !fields) return undefined;

  const fieldsByKey = keyBy(
    fields.filter(
      (field) =>
        !field.isDeleted &&
        SERVER_GROUPABLE_FIELD_TYPES.has(field.fieldType) &&
        !NON_GROUPABLE_DISPLAY_TYPES.has(field.displayType) &&
        !CLIENT_ONLY_GROUPABLE_DISPLAY_TYPES.has(field.displayType)
    ),
    (field) => camelizeFieldKey(field.fieldKey)
  );

  const field =
    fieldsByKey[groupField] ??
    (groupField.endsWith("Name")
      ? fieldsByKey[`${groupField.slice(0, -"Name".length)}Id`]
      : undefined);

  return field
    ? {
        fieldKey: camelizeFieldKey(field.fieldKey),
        displayType: field.displayType
      }
    : undefined;
};

/**
 * Labels a date column's month, quarter or year group option. A table passes one only
 * when it builds the quarter and year grouping columns (`buildDateBucketColumn`), since
 * TanStack drops a grouping key with no column behind it.
 */
export type DateBucketOptionLabel = (
  label: string,
  bucket: DateBucket
) => string;

const DATE_GROUPED_DISPLAY_TYPES: ReadonlySet<string> = new Set([
  FieldDisplayType.DATE,
  FieldDisplayType.DATE_RANGE
]);

/**
 * Only a custom or formula column groups its amounts by currency (its `currencyGroupCode`
 * meta). A built-in amount column has no such meta and would get one group per figure.
 */
const CURRENCY_GROUPED_FIELD_TYPES: ReadonlySet<SettingsFieldType> = new Set([
  SettingsFieldType.CUSTOM,
  SettingsFieldType.FORMULA
]);

/** One option for the column, or one per date bucket when the table offers them. */
const groupByOptionsForColumn = (
  columnId: string,
  label: string,
  isDate: boolean,
  dateBucketLabel: DateBucketOptionLabel | undefined
): GroupByOption[] =>
  isDate && dateBucketLabel
    ? DATE_BUCKETS.map((bucket) => ({
        id:
          bucket === "month"
            ? columnId
            : `${columnId}${DATE_BUCKET_SEPARATOR}${bucket}`,
        displayName: dateBucketLabel(label, bucket)
      }))
    : [{ id: columnId, displayName: label }];

// `fallbackColumns`: columns the picker can still reveal beyond the persisted set.
export const mapFieldsToGroupByOptions = (
  tableColumns: ColumnData[],
  customFields: APIFieldItem[] | undefined,
  fallbackColumns: ColumnData[] = [],
  dateBucketLabel?: DateBucketOptionLabel
): GroupByOption[] => {
  if (!customFields) return [];

  const groupableFields = filterFieldsForFilterOptions(customFields).filter(
    (field) =>
      !NON_GROUPABLE_DISPLAY_TYPES.has(field.displayType) &&
      (field.displayType !== FieldDisplayType.CURRENCY ||
        CURRENCY_GROUPED_FIELD_TYPES.has(field.fieldType))
  );
  const fieldsByKey = keyBy(groupableFields, (field) =>
    camelizeFieldKey(field.fieldKey)
  );

  const fieldForColumnId = (columnId: string): APIFieldItem | undefined =>
    fieldsByKey[columnId] ??
    (columnId.endsWith("Name")
      ? fieldsByKey[`${columnId.slice(0, -"Name".length)}Id`]
      : undefined);

  // One option per registry field, keyed by the column that surfaces it: TanStack drops a
  // grouping key with no column, and two columns can resolve to one field (`productId`
  // directly, `productName` through the fallback) which would offer the same concept twice.
  const matchedFieldKeys = new Set<string>();
  const collectOptions = (columns: ColumnData[]): GroupByOption[] =>
    columns.reduce<GroupByOption[]>((options, column) => {
      const field = fieldForColumnId(column.id);
      if (!field) return options;

      const fieldKey = camelizeFieldKey(field.fieldKey);
      if (matchedFieldKeys.has(fieldKey)) return options;

      matchedFieldKeys.add(fieldKey);
      // The column's own label — an alias row's registry label diverges from the header.
      options.push(
        ...groupByOptionsForColumn(
          column.id,
          column.label || field.label,
          DATE_GROUPED_DISPLAY_TYPES.has(field.displayType),
          dateBucketLabel
        )
      );
      return options;
    }, []);

  // Live columns first, so their label and order win over what the defaults can still reveal.
  return [...collectOptions(tableColumns), ...collectOptions(fallbackColumns)];
};

/**
 * A filter that picks from a closed set of values (a list, an enum, yes/no, or a date the
 * table buckets by month, quarter or year) groups the table into a bounded number of groups. Free text and
 * numbers give roughly one group per row, so their columns are left out.
 */
const isGroupableFilter = ({
  filterType,
  displayType
}: DataTableFilter): boolean => {
  if (displayType && NON_GROUPABLE_DISPLAY_TYPES.has(displayType)) return false;
  switch (filterType.type) {
    case "map":
    case "enum":
    case "boolean":
    case "date":
      return true;
    case "customField":
      return !NON_GROUPABLE_DISPLAY_TYPES.has(
        filterType.values.field.displayType
      );
    default:
      return false;
  }
};

/**
 * Group-by options for every column the table can filter by a groupable filter. A filter
 * reaches its column through `columnId`, its own key, or the column showing the name its id
 * key resolves to (`companyId` filters the `companyName` column).
 */
export const mapFiltersToGroupByOptions = (
  filterOptions: DataTableFilter[] | undefined,
  tableColumns: ColumnData[],
  fallbackColumns: ColumnData[] = [],
  dateBucketLabel?: DateBucketOptionLabel
): GroupByOption[] => {
  if (!filterOptions) return [];

  // Live columns first, so their label wins over the default's.
  const columnsById = new Map<string, ColumnData>();
  [...tableColumns, ...fallbackColumns].forEach((column) => {
    if (!columnsById.has(column.id)) columnsById.set(column.id, column);
  });

  const columnForFilter = ({
    key,
    columnId
  }: DataTableFilter): ColumnData | undefined => {
    const candidates = [
      columnId,
      key,
      key.endsWith("Id") ? `${key.slice(0, -"Id".length)}Name` : undefined
    ];
    return candidates
      .map((id) => (id ? columnsById.get(id) : undefined))
      .find(Boolean);
  };

  const options = new Map<string, GroupByOption>();
  filterOptions.filter(isGroupableFilter).forEach((filter) => {
    const column = columnForFilter(filter);
    if (!column || options.has(column.id)) return;
    groupByOptionsForColumn(
      column.id,
      tWithFallback(
        `datatoolbar.filters.sortAndGroupByOptions.${column.id}`,
        column.label || filter.customFieldLabel || column.id
      ),
      filter.filterType.type === "date",
      dateBucketLabel
    ).forEach((option) => options.set(option.id, option));
  });
  return [...options.values()];
};

export const getIsCheckboxDisabled = (
  column: ColumnData,
  columnsLength: number
): boolean => {
  if (column.id === "name" || column.id === "fullName") return true;
  if (columnsLength <= 1) return true;
  return false;
};

/** The lockable ids forming the front of `orderedIds`, in any order; skipped ids neither join nor end that run. */
export const getLeadingLockedIds = (
  orderedIds: string[],
  lockableIds: ReadonlySet<string>,
  isSkipped: (id: string) => boolean = () => false
): Set<string> => {
  const leading = new Set<string>();
  for (const id of orderedIds) {
    if (isSkipped(id)) continue;
    if (!lockableIds.has(id)) break;
    leading.add(id);
  }
  return leading;
};

export const handleColumnChange = (
  column: ColumnData,
  checked: boolean,
  columnsLength: number,
  setColumnStates: (updater: (prev: ColumnData[]) => ColumnData[]) => void
) => {
  if (!checked && columnsLength <= 1) return;

  setColumnStates((currentColumns) =>
    currentColumns.map((c) =>
      c.id === column.id ? { ...c, isVisible: checked } : c
    )
  );
};
